import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User, LoginCredentials, RegisterCredentials } from '../types';
import { authApi, setToken, removeToken, getToken } from '../services/api';

interface AuthState {
  // State
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (credentials: RegisterCredentials) => Promise<void>;
  logout: () => Promise<void>;
  loadUser: () => Promise<void>;
  clearError: () => void;
  setUser: (user: User) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Login action
      login: async (credentials: LoginCredentials) => {
        set({ isLoading: true, error: null });
        
        try {
          const response = await authApi.login(credentials.email, credentials.password);
          
          if (response.success && response.data) {
            const { user, token } = response.data;
            setToken(token);
            
            set({
              user,
              token,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });
          }
        } catch (error: unknown) {
          const message = error instanceof Error 
            ? error.message 
            : (error && typeof error === 'object' && 'message' in error) 
              ? String(error.message) 
              : 'Bejelentkezés sikertelen';
          set({
            isLoading: false,
            error: message,
          });
          throw error;
        }
      },

      // Register action
      register: async (credentials: RegisterCredentials) => {
        set({ isLoading: true, error: null });
        
        try {
          const response = await authApi.register(
            credentials.email,
            credentials.password,
            credentials.name
          );
          
          if (response.success && response.data) {
            const { user, token } = response.data;
            setToken(token);
            
            set({
              user,
              token,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });
          }
        } catch (error: unknown) {
          const message = error instanceof Error 
            ? error.message 
            : (error && typeof error === 'object' && 'message' in error) 
              ? String(error.message) 
              : 'Regisztráció sikertelen';
          set({
            isLoading: false,
            error: message,
          });
          throw error;
        }
      },

      // Logout action
      logout: async () => {
        try {
          await authApi.logout();
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          removeToken();
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            error: null,
          });
        }
      },

      // Load user from token
      loadUser: async () => {
        const token = getToken() || get().token;
        
        if (!token) {
          set({ isLoading: false });
          return;
        }

        setToken(token);
        set({ isLoading: true, token, isAuthenticated: true });

        try {
          const response = await authApi.me();
          
          if (response.success && response.data) {
            set({
              user: response.data.user,
              token,
              isAuthenticated: true,
              isLoading: false,
            });
          }
        } catch (error: unknown) {
          // Network errors should not wipe auth state
          if (error && typeof error === 'object' && 'code' in error && error.code === 'NETWORK_ERROR') {
            set({
              token,
              isAuthenticated: true,
              isLoading: false,
            });
            return;
          }

          removeToken();
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      // Clear error
      clearError: () => set({ error: null }),

      // Set user (for profile updates)
      setUser: (user: User) => set({ user }),
    }),
    {
      name: 'paingain-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        token: state.token,
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.token) {
          setToken(state.token);
        }
        state?.loadUser?.();
      },
    }
  )
);

// Listen for auth:logout events
if (typeof window !== 'undefined') {
  window.addEventListener('auth:logout', () => {
    useAuthStore.getState().logout();
  });
}
